import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { localStorageToken } from '../tokens/localStorage.token';

/**
 * Service for managing the application's dark mode state. Provides methods for toggling and retrieving
 * the dark mode status. The service holds the current dark mode state, allowing access to the value
 * from any part of the application.
 *
 * PrimeNG v18: Dark mode is implemented by toggling the '.darkMode' class on the document root
 * (html element), which is configured via providePrimeNG's darkModeSelector option.
 *
 * @usageNotes
 * - Use the `toggleDarkMode` method to switch between dark and light modes.
 * - Use the `getDarkMode` method to retrieve the current dark mode status.
 * - Dark mode preference is persisted to localStorage and restored on app initialization.
 */

export const DARK_MODE_CLASS = 'darkMode';

@Injectable({
    providedIn: 'root',
})
export class DarkModeService {
    private readonly DARK_MODE_STORAGE_KEY = 'lemunzo-dark-mode';
    private darkMode: boolean = false;

    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        @Inject(localStorageToken) private readonly localStorage: Storage
    ) {
        // Initialize dark mode state from localStorage (persisted preference)
        const storedDarkMode = this.localStorage.getItem(
            this.DARK_MODE_STORAGE_KEY
        );
        this.darkMode =
            storedDarkMode !== null ? storedDarkMode === 'true' : false;

        // Apply the class to document root for PrimeNG v18 theme system
        this.applyDarkModeClass();
    }

    /**
     * Toggles the dark mode status by adding/removing the '.darkMode' class on the document root.
     * PrimeNG v18 uses the darkModeSelector option to detect this class and apply dark theme tokens.
     * The preference is persisted to localStorage.
     */
    toggleDarkMode(): void {
        this.darkMode = !this.darkMode;
        this.applyDarkModeClass();
        this.persistDarkModePreference();
    }

    /**
     * @returns Whether dark mode is active
     */
    getDarkMode(): boolean {
        return this.darkMode;
    }

    /**
     * Applies or removes the darkMode class on the document root element.
     * PrimeNG v18's darkModeSelector detects this class to apply dark theme tokens.
     */
    private applyDarkModeClass(): void {
        const htmlElement = this.document.documentElement;
        if (this.darkMode) {
            htmlElement.classList.add(DARK_MODE_CLASS);
        } else {
            htmlElement.classList.remove(DARK_MODE_CLASS);
        }
    }

    /**
     * Persists the dark mode preference to localStorage.
     */
    private persistDarkModePreference(): void {
        this.localStorage.setItem(
            this.DARK_MODE_STORAGE_KEY,
            this.darkMode.toString()
        );
    }
}
